import * as React from 'react'
import classNames from 'classnames'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import './underlineTabs.scss'

export interface UnderlineTab<T extends string> {
    id: T
    label: string
    badge?: string | number
    disabled?: boolean
    disabledReason?: string
}

interface UnderlineTabsProps<T extends string> {
    tabs: UnderlineTab<T>[]
    value: T
    onChange: (value: T) => void
    ariaLabel: string
    idPrefix: string
    className?: string
}

export function UnderlineTabs<T extends string>({
    tabs,
    value,
    onChange,
    ariaLabel,
    idPrefix,
    className,
}: UnderlineTabsProps<T>) {
    const buttons = React.useRef<Record<string, HTMLButtonElement | null>>({})
    const selectable = tabs.filter((tab) => !tab.disabled)

    const pick = (tab: UnderlineTab<T>) => {
        onChange(tab.id)
        buttons.current[tab.id]?.focus()
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const current = selectable.findIndex((tab) => tab.id === value)
        const count = selectable.length
        if (count === 0) {
            return
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault()
            pick(selectable[(current + 1) % count])
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault()
            pick(selectable[(current - 1 + count) % count])
        } else if (event.key === 'Home') {
            event.preventDefault()
            pick(selectable[0])
        } else if (event.key === 'End') {
            event.preventDefault()
            pick(selectable[count - 1])
        }
    }

    return (
        <div
            role="tablist"
            aria-label={ariaLabel}
            className={classNames('underline-tabs', className)}
            onKeyDown={onKeyDown}
        >
            {tabs.map((tab) => {
                const selected = tab.id === value
                const button = (
                    <button
                        key={tab.id}
                        ref={(element) => {
                            buttons.current[tab.id] = element
                        }}
                        type="button"
                        role="tab"
                        id={`${idPrefix}-tab-${tab.id}`}
                        aria-selected={selected}
                        aria-controls={`${idPrefix}-panel-${tab.id}`}
                        aria-disabled={tab.disabled || undefined}
                        tabIndex={selected ? 0 : -1}
                        className={classNames('underline-tab', { selected, disabled: tab.disabled })}
                        onClick={() => !tab.disabled && onChange(tab.id)}
                    >
                        <span className="underline-tab-label">{tab.label}</span>
                        {tab.badge !== undefined && <span className="underline-tab-badge">{tab.badge}</span>}
                    </button>
                )
                return tab.disabled && tab.disabledReason ? (
                    <DisabledItemHoverInfotip key={tab.id} active infotipText={tab.disabledReason} width="18em" bottom>
                        {button}
                    </DisabledItemHoverInfotip>
                ) : (
                    button
                )
            })}
        </div>
    )
}
